import type { AggregateValue, GroupKey, GroupKeyPart } from '@ofmos/shared/record-group';
import type { ReportDefinition } from '@ofmos/shared/report';
import { fillBuckets, resolveReportWindow } from '@ofmos/shared/report-series';
import type { FieldMetadata, RecordGroupEntry, RecordGroupSet } from '../../data/client';
import type { ChartPoint, ChartSeries, TagTone } from '../../ui';
import { groupLabel, groupTone, resolveDimension } from '../records/table/groupModel';

/**
 * THE RUN RESULT, AS SOMETHING A CHART CAN DRAW (`docs/decisions/reports.md` T4).
 *
 * Three jobs, and none of them is arithmetic: name every bucket in the reader's own words,
 * fill the buckets the database returned nothing for, and split the result into series
 * when the report carries a segment.
 *
 * LABELS COME FROM THE TABLE'S OWN FORMATTER (`groupModel.ts`), not from a second one
 * here. A month that reads "August 2026" in a grouped table and "2026-08" on a chart would
 * be the same bucket wearing two names, and the first person to notice would be right to
 * distrust both.
 *
 * THE FILL IS THE SHARED HELPER, over the window the report ASKED for rather than over the
 * data that came back - which is the only way to know that September was in the question.
 * A chart drawn straight from the response joins August to October and reads as a smooth
 * slope where the truth is a hole.
 */

export interface ReportChartData {
  readonly series: readonly ChartSeries[];
  /** The single dimension's points, in order: what a donut, a funnel and a takeaway read. */
  readonly points: readonly ChartPoint[];
  /** The one bucket a big number reads, or null when the report has a dimension. */
  readonly total: number | null;
}

function scalar(value: AggregateValue | undefined): number | null {
  /**
   * A CURRENCY aggregate is a set of per-code amounts rather than one number (D2), and a
   * chart with one axis cannot plot two currencies. Reading the first code would be a
   * silent unit error, so it reads as no value and the hidden table says "No data".
   */
  if (value?.kind !== 'value') return null;
  if (value.value === null) return null;
  const numeric = Number(value.value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** The key part identifying a bucket, for the fill. Non-bucket dimensions have none. */
function bucketStartOf(part: GroupKeyPart | undefined): string | null {
  return part?.kind === 'bucket' ? part.start : null;
}

function keyOf(key: GroupKey, position: number): string {
  const part = key[position];
  if (part === undefined) return 'none';
  switch (part.kind) {
    case 'null':
      return 'null';
    case 'option':
      return part.optionId;
    case 'boolean':
      return String(part.value);
    case 'relation':
      return part.recordId;
    case 'bucket':
      return part.start;
  }
}

export interface ReportSeriesInput {
  readonly definition: ReportDefinition;
  readonly result: RecordGroupSet;
  readonly fields: readonly FieldMetadata[];
  /** The day the range was resolved against, so the fill covers what was asked for. */
  readonly today: Date;
}

export function reportChartData(input: ReportSeriesInput): ReportChartData {
  const { definition, result, fields } = input;

  /**
   * NO DIMENSION IS ONE BUCKET, which is what a big number is: the zero-dimension read
   * returns exactly one group over the filtered set.
   */
  if (definition.groupBy === undefined) {
    const only = result.groups[0];
    const total = scalar(only?.aggregates[0]);
    return {
      series: [],
      points: total === null ? [] : [{ key: 'total', label: 'Total', value: total }],
      total,
    };
  }

  const primary = resolveDimension([toDimension(definition.groupBy)], fields);
  const segment =
    definition.segmentBy === undefined
      ? null
      : resolveDimension([toDimension(definition.segmentBy)], fields);

  const labelOf = (entry: RecordGroupEntry, position: number): string => {
    const resolved = position === 0 ? primary : segment;
    if (resolved === null) return 'Unknown';
    // `groupLabel` reads the FIRST part of the key it is given, so a segment's part is
    // handed over as a one-part key rather than by index.
    return groupLabel(
      [entry.key[position]].filter((part): part is GroupKeyPart => part !== undefined),
      resolved,
      [entry.labels[position] ?? null],
    );
  };
  const toneOf = (entry: RecordGroupEntry, position: number): TagTone | undefined => {
    const resolved = position === 0 ? primary : segment;
    if (resolved === null) return undefined;
    const tone = groupTone(
      [entry.key[position]].filter((part): part is GroupKeyPart => part !== undefined),
      resolved,
    );
    return tone ?? undefined;
  };

  /** The categories, in the order the server returned them - which is the sort it applied. */
  const categories = new Map<string, ChartPoint>();
  for (const entry of result.groups) {
    const key = keyOf(entry.key, 0);
    if (categories.has(key)) continue;
    const tone = toneOf(entry, 0);
    categories.set(key, {
      key,
      label: labelOf(entry, 0),
      value: null,
      ...(tone === undefined ? {} : { tone }),
    });
  }

  if (segment === null) {
    const points = [...categories.values()].map((point) => {
      const entry = result.groups.find((group) => keyOf(group.key, 0) === point.key);
      return { ...point, value: scalar(entry?.aggregates[0]) };
    });
    const filled = fillTimeSeries(points, input);
    return {
      series: [{ id: 'metric', label: 'Value', points: filled }],
      points: filled,
      total: null,
    };
  }

  /** One series per segment value, every one carrying every category so the axis lines up. */
  const segments = new Map<string, { label: string; tone?: TagTone }>();
  for (const entry of result.groups) {
    const key = keyOf(entry.key, 1);
    if (segments.has(key)) continue;
    const tone = toneOf(entry, 1);
    segments.set(key, { label: labelOf(entry, 1), ...(tone === undefined ? {} : { tone }) });
  }

  const categoryPoints = fillTimeSeries([...categories.values()], input);
  const series: ChartSeries[] = [...segments.entries()].map(([segmentKey, meta]) => ({
    id: segmentKey,
    label: meta.label,
    ...(meta.tone === undefined ? {} : { tone: meta.tone }),
    points: categoryPoints.map((category) => {
      const entry = result.groups.find(
        (group) => keyOf(group.key, 0) === category.key && keyOf(group.key, 1) === segmentKey,
      );
      return { ...category, value: scalar(entry?.aggregates[0]) };
    }),
  }));

  return { series, points: categoryPoints, total: null };
}

function toDimension(dimension: ReportDefinition['groupBy']) {
  if (dimension === undefined) throw new Error('a dimension is required here');
  return dimension.granularity === undefined
    ? { fieldId: dimension.fieldId }
    : { fieldId: dimension.fieldId, granularity: dimension.granularity };
}

/**
 * The empty-bucket fill (T4), applied only where there ARE buckets: a report grouped by a
 * SELECT has a closed domain the server already enumerated, and inventing categories for
 * it would be inventing options.
 */
function fillTimeSeries(
  points: readonly ChartPoint[],
  input: ReportSeriesInput,
): readonly ChartPoint[] {
  const granularity = input.definition.groupBy?.granularity;
  if (granularity === undefined) return points;
  const window = resolveReportWindow(input.definition.range, input.today);
  if (window === null) return points;

  const byStart = new Map(points.map((point) => [point.key, point]));
  const filled = fillBuckets(
    points.map((point) => ({ bucketStart: point.key, value: point.value })),
    { granularity, from: window.from, to: window.to, op: input.definition.metric.op },
  );
  return filled.map((bucket) => {
    const known = byStart.get(bucket.bucketStart);
    return (
      known ?? {
        key: bucket.bucketStart,
        // A bucket the server never returned has no label from the server either, so it is
        // formatted from its own key by the same formatter the others went through.
        label: groupLabel(
          [{ kind: 'bucket', granularity, start: bucket.bucketStart }],
          {
            dimension: { fieldId: input.definition.groupBy?.fieldId ?? '', granularity },
            field: input.fields[0] as FieldMetadata,
            kind: 'bucketed',
          },
          [null],
        ),
        value: bucket.value,
      }
    );
  });
}

/** What the reader is told a bucket start means. Exported for the tests and the table. */
export function bucketStartsOf(groups: readonly RecordGroupEntry[]): readonly string[] {
  return groups.flatMap((group) => {
    const start = bucketStartOf(group.key[0]);
    return start === null ? [] : [start];
  });
}
